/**
 * One founder-readable sentence per safe failure code.
 *
 * SEPARATE FROM THE TRANSPORT ON PURPOSE. This is presentation copy — a status
 * card needs it to render a failed run, and dragging the Supabase client (and
 * through it `expo-secure-store`) into a component that only wants a sentence is
 * both wrong and untestable.
 *
 * No message names a provider, a table, a model or an internal code. A raw
 * upstream message can carry a request URL, and a request URL here carries the
 * query text.
 */

import type { SafeFailureCode } from './types';

export const FAILURE_MESSAGES: Record<SafeFailureCode, string> = {
  provider_unavailable: 'A research source was unavailable. Try again shortly.',
  input_not_ready: 'Some analysis inputs still need your review.',
  source_detail_unavailable: 'This source cannot be shown here.',
  insufficient_evidence: 'There was not enough supporting evidence to publish this analysis.',
  invalid_model_output: 'The analysis could not be produced. Try again.',
  // Our output ceiling, not the founder's inputs and not the sources. Says
  // "try again" for the same reason — the retry is smaller — without implying
  // anything was wrong with what they gave us.
  response_truncated: 'The analysis ran longer than one response allows. Try again.',
  claim_validation_failed: 'The analysis could not be verified against its sources.',
  timed_out: 'The analysis took too long and was stopped. You can start it again.',
  consent_required: 'Consent is required before this analysis can run.',
  feature_disabled: 'Strategic analysis is not available for this account yet.',
  rate_limited: 'Too many requests. Try again in a moment.',
  usage_limit_reached: 'You have used all of this month’s strategic analyses.',
  // Ours, not the research's. The wording deliberately does not say "not enough
  // evidence" — the evidence may be fine, and sending a founder to look for data
  // that already exists would be a lie about whose fault this is.
  // FOUNDER-SAFE. Names no provider and no internal mechanism: which vendor
  // misbehaved is an operational detail, and telling a founder "Tavily returned
  // a malformed record" is both meaningless to them and a disclosure about our
  // supply chain.
  invalid_provider_output:
    'One of our research sources returned data we could not verify, so nothing was published. This is a fault on our side — please try again, and contact support if it repeats.',
  report_contract_failed: 'The analysis finished but could not be published in a readable form. This is a fault on our side — please try again, and contact support if it repeats.',
  internal_error: 'Something went wrong. Try again.',
};

/**
 * What the reader is told when a PUBLISHED report cannot be read back.
 *
 * Two different faults, two different actions, and neither of them is "no
 * analysis has been generated" — which is what both used to render.
 */
export const REPORT_READ_MESSAGES = {
  /** The report is newer than this build. Reloading is the actual fix. */
  unsupported_version:
    'This report was produced by a newer version of Inverio than the one you are running. Reload the page to pick up the latest version.',
  /**
   * A well-formed report from an OLDER contract.
   *
   * Refreshing IS the fix, and saying so is the point: the report is intact, it
   * simply predates per-node citations, identity provenance and the labels that
   * separate a finding from an AI consideration. Rendering it through today's
   * components would show it with none of those, which reads as a report whose
   * claims have no sources rather than as a report from before sources were
   * attached to claims.
   */
  legacy_report:
    'This analysis was produced before the current evidence contract, so its citations and provenance labels are not available to display. Refreshing it produces a report with sources attached to every finding.',
  /** The stored body failed its own contract. Nothing the reader can do. */
  malformed:
    'This report was published in a form we cannot display. The analysis itself is unaffected — regenerating it usually resolves this, and support can look at the stored copy.',
} as const;

/** An unrecognised code falls back rather than being rendered as itself. */
export function messageFor(code: SafeFailureCode | string): string {
  return FAILURE_MESSAGES[code as SafeFailureCode] ?? FAILURE_MESSAGES.internal_error;
}

export const KNOWN_FAILURE_CODES: ReadonlySet<string> = new Set(Object.keys(FAILURE_MESSAGES));

/**
 * A failure's SAFE code, never its message.
 *
 * DUCK-TYPED, deliberately. `StrategicAnalysisError` lives in the transport,
 * which imports the Supabase client and through it `expo-secure-store` — so a
 * component that only wants a sentence cannot `instanceof` it without dragging
 * the whole transport into its module graph. The shape is the contract: a name
 * and a code from the known vocabulary.
 *
 * Anything else becomes `internal_error`, because an error shape nobody designed
 * is exactly the one likely to carry a request URL, and a request URL here
 * carries the query text.
 */
export function safeFailureCodeOf(error: unknown): SafeFailureCode {
  if (typeof error !== 'object' || error === null) return 'internal_error';
  const candidate = (error as { code?: unknown }).code;
  return typeof candidate === 'string' && KNOWN_FAILURE_CODES.has(candidate)
    ? (candidate as SafeFailureCode)
    : 'internal_error';
}
